import type { FormulaReferenceField } from "./customFormulaParser";
import { predefinedCustomFormulaFunctions } from "./customFormulaParser";
import {
  countUnescapedQuotes,
  escapeFormulaQuotedValue,
  findUnescapedQuoteBackward,
  findUnescapedQuoteForward,
} from "./customFormulaQuotedValue";

export interface FormulaSuggestion {
  key: string;
  kind: "field" | "function";
  label: string;
  insertText: string;
  guid?: string;
}

export interface FormulaSuggestionContext {
  query: string;
  insideQuotes: boolean;
}

interface ActiveFormulaFragment {
  text: string;
  start: number;
  end: number;
  insideQuotes: boolean;
}

interface ApplySuggestionResult {
  value: string;
  caretIndex: number;
}

const clampCaretIndex = (input: string, caretIndex: number): number => {
  if (caretIndex < 0) {
    return 0;
  }
  if (caretIndex > input.length) {
    return input.length;
  }
  return caretIndex;
};

const isInsideQuotedFragment = (input: string, caretIndex: number): boolean => {
  const inputBeforeCaret = input.slice(0, caretIndex);
  return countUnescapedQuotes(inputBeforeCaret) % 2 === 1;
};

const isIdentifierFragmentCharacter = (character: string | undefined): boolean => {
  if (character === undefined) {
    return false;
  }
  return /[A-Za-z0-9_]/.test(character);
};

const getQuotedFragment = (input: string, caretIndex: number): ActiveFormulaFragment => {
  const quoteStartIndex = findUnescapedQuoteBackward(input, caretIndex - 1);
  const quoteEndIndex = findUnescapedQuoteForward(input, caretIndex);

  const start = quoteStartIndex >= 0 ? quoteStartIndex + 1 : caretIndex;
  const end = quoteEndIndex >= 0 ? quoteEndIndex : input.length;

  return {
    text: input.slice(start, caretIndex),
    start,
    end,
    insideQuotes: true,
  };
};

const getIdentifierFragment = (input: string, caretIndex: number): ActiveFormulaFragment => {
  let start = caretIndex;
  while (start > 0 && isIdentifierFragmentCharacter(input[start - 1])) {
    start--;
  }

  let end = caretIndex;
  while (end < input.length && isIdentifierFragmentCharacter(input[end])) {
    end++;
  }

  return {
    text: input.slice(start, caretIndex),
    start,
    end,
    insideQuotes: false,
  };
};

const getActiveFormulaFragment = (input: string, caretIndex: number): ActiveFormulaFragment => {
  if (isInsideQuotedFragment(input, caretIndex)) {
    return getQuotedFragment(input, caretIndex);
  }
  return getIdentifierFragment(input, caretIndex);
};

const rankSuggestion = (query: string, label: string): number => {
  if (query.length === 0) {
    return 3;
  }

  const queryLower = query.toLowerCase();
  const labelLower = label.toLowerCase();
  if (labelLower.startsWith(queryLower)) {
    return 0;
  }
  if (labelLower.includes(queryLower)) {
    return 1;
  }
  return 2;
};

const isSuggestionMatch = (query: string, label: string, alternateValue?: string): boolean => {
  if (query.length === 0) {
    return true;
  }

  const queryLower = query.toLowerCase();
  const labelLower = label.toLowerCase();
  if (labelLower.includes(queryLower)) {
    return true;
  }

  if (!alternateValue) {
    return false;
  }

  return alternateValue.toLowerCase().includes(queryLower);
};

const compareSuggestions = (query: string, first: FormulaSuggestion, second: FormulaSuggestion): number => {
  const rankDiff = rankSuggestion(query, first.label) - rankSuggestion(query, second.label);
  if (rankDiff !== 0) {
    return rankDiff;
  }

  if (first.kind !== second.kind) {
    return first.kind === "field" ? -1 : 1;
  }

  return first.label.localeCompare(second.label);
};

const createFieldSuggestion = (field: FormulaReferenceField): FormulaSuggestion => {
  return {
    key: `field:${field.guid}`,
    kind: "field",
    label: field.caption,
    insertText: `"${escapeFormulaQuotedValue(field.caption)}"`,
    guid: field.guid,
  };
};

const createFunctionSuggestion = (name: string): FormulaSuggestion => {
  return {
    key: `function:${name}`,
    kind: "function",
    label: name,
    insertText: `${name}(`,
  };
};

const getReplacementRange = (input: string, caretIndex: number): ActiveFormulaFragment => {
  return getActiveFormulaFragment(input, caretIndex);
};

const replaceRange = (input: string, start: number, end: number, replacement: string): string => {
  return `${input.slice(0, start)}${replacement}${input.slice(end)}`;
};

export const getFormulaSuggestionContext = (input: string, caretIndex: number): FormulaSuggestionContext => {
  const safeCaretIndex = clampCaretIndex(input, caretIndex);
  const fragment = getActiveFormulaFragment(input, safeCaretIndex);

  return {
    query: fragment.text.trim().toLowerCase(),
    insideQuotes: fragment.insideQuotes,
  };
};

export const getFormulaSuggestions = (
  input: string,
  caretIndex: number,
  fields: FormulaReferenceField[],
  functions: string[] = predefinedCustomFormulaFunctions
): FormulaSuggestion[] => {
  const suggestionContext = getFormulaSuggestionContext(input, caretIndex);
  const query = suggestionContext.query;

  const fieldSuggestions = fields.filter((field) => isSuggestionMatch(query, field.caption)).map(createFieldSuggestion);

  if (suggestionContext.insideQuotes) {
    return fieldSuggestions.sort((a, b) => compareSuggestions(query, a, b));
  }

  const functionSuggestions = functions.filter((name) => isSuggestionMatch(query, name)).map(createFunctionSuggestion);

  return [...fieldSuggestions, ...functionSuggestions].sort((a, b) => compareSuggestions(query, a, b));
};

export const applyFormulaSuggestionAtCaret = (
  input: string,
  caretIndex: number,
  suggestion: FormulaSuggestion
): ApplySuggestionResult => {
  const safeCaretIndex = clampCaretIndex(input, caretIndex);
  const range = getReplacementRange(input, safeCaretIndex);
  const replacementStart = range.insideQuotes ? range.start - 1 : range.start;
  const replacementEnd = range.insideQuotes ? range.end + 1 : range.end;
  const nextValue = replaceRange(input, replacementStart, replacementEnd, suggestion.insertText);
  const nextCaretIndex = replacementStart + suggestion.insertText.length;

  return { value: nextValue, caretIndex: nextCaretIndex };
};
